/**
 * rasterThinSkeleton.ts
 *
 * A raster medial-axis skeleton for a wall region (an outline with the rooms
 * punched out as holes). Instead of the exact straight-skeleton wavefront
 * simulation — which is fragile on noisy, near-degenerate point-cloud floor
 * plans (simultaneous events make the spine zig-zag and drift) — this works on
 * the FILLED PIXELS of the wall:
 *
 *   1. Rasterize the wall region into a binary grid (wall = 1, room/void = 0).
 *   2. Zhang–Suen thinning erodes the wall to a 1-pixel-wide centerline
 *      skeleton (the same technique as https://skeleton-tracing.netlify.app).
 *   3. Trace the thinned pixels into polylines (split at junctions / ends).
 *   4. Douglas–Peucker simplify each polyline, then snap near-axis segments to
 *      true horizontal/vertical (floor plans are mostly rectilinear).
 *
 * The result is a clean, centered spine that follows the wall regardless of how
 * jagged the input polygons are — it never leaves the wall (it can only live on
 * filled pixels) and never zig-zags (thinning is a local, stable operation).
 *
 * The grid is SPARSE: pixels live in 64×64 tiles allocated only where wall
 * pixels actually exist. Wall pixels are proportional to wall AREA (total wall
 * length × thickness), which is tiny compared to a large floor's bounding box —
 * so the cell size never has to coarsen just because the floor is big, and a
 * many-room floor keeps full resolution. (The previous dense grid capped the
 * BOUNDING-BOX cell count at 4M and silently coarsened past ~1,600 m², which
 * eroded thin walls to sub-pixel width and broke wall creation on large plans.)
 *
 * Coordinates: input/output are world units (Vec2). Internally everything runs
 * on an integer grid; we map back to world coords on the way out.
 */

/** A 2D point / vector. Plain object so this module stays THREE-free. */
export interface Vec2 {
  x: number;
  y: number;
}

/** One straight segment of the resulting skeleton. */
export interface SkeletonArc {
  a: Vec2;
  b: Vec2;
}

export type Ring = Vec2[];

export interface RasterSkeletonInput {
  /** Outer boundary loop (filled). */
  outer: Ring;
  /** Hole loops (rooms / cavities) — cleared from the fill. */
  holes?: Ring[];
}

/**
 * Snapshot of the raster grids for debug visualization. `wall` is the filled
 * wall region (1 = wall) before thinning; `thinned` is the 1-pixel skeleton.
 * Both are row-major Uint8Array of size `w*h`; cell (gx,gy) maps to world
 * `(ox + (gx+0.5)*cell, oy + (gy+0.5)*cell)`.
 */
export interface RasterDebug {
  w: number;
  h: number;
  cell: number;
  ox: number;
  oy: number;
  wall: Uint8Array;
  thinned: Uint8Array;
}

export interface RasterSkeletonOptions {
  /**
   * Grid cell size in world units. Smaller = finer skeleton but more pixels.
   * If omitted, chosen automatically from the estimated wall thickness so thin
   * walls survive thinning (~4 cells across the thinnest wall).
   */
  cellSize?: number;
  /** Douglas–Peucker tolerance in world units (default: 1.5 × cellSize). */
  simplifyTolerance?: number;
  /** Max deviation (degrees) for a segment to be snapped to an axis (default: 8). */
  axisSnapAngleDeg?: number;
  /**
   * Failsafe cap on ALLOCATED wall cells (default: 128,000,000 ≈ 128 MB of
   * tiles). The grid is sparse, so floor bounding-box size does not count
   * against this — only actual wall pixels do. Realistic plans stay far below
   * it; the cell size coarsens only if the wall raster itself would exceed it.
   */
  maxCells?: number;
  /** Receives the raster grids for debug overlay (filled walls + thinned skeleton). */
  onDebug?: (debug: RasterDebug) => void;
}

// --- geometry helpers ------------------------------------------------------

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Bounding box of all rings. */
const bounds = (rings: Ring[]): { minx: number; miny: number; maxx: number; maxy: number } => {
  let minx = Infinity;
  let miny = Infinity;
  let maxx = -Infinity;
  let maxy = -Infinity;
  for (const ring of rings) {
    for (const p of ring) {
      minx = Math.min(minx, p.x);
      miny = Math.min(miny, p.y);
      maxx = Math.max(maxx, p.x);
      maxy = Math.max(maxy, p.y);
    }
  }
  return { minx, miny, maxx, maxy };
};

/**
 * Estimate the wall thickness: for a handful of sample points on the outline,
 * the distance to the nearest hole edge approximates the local wall depth. We
 * take a robust small-percentile so the cell size resolves the THINNEST walls.
 * Falls back to a fraction of the bbox when there are no holes.
 */
const estimateWallThickness = (outer: Ring, holes: Ring[], bbox: { minx: number; miny: number; maxx: number; maxy: number }): number => {
  const span = Math.min(bbox.maxx - bbox.minx, bbox.maxy - bbox.miny);
  if (holes.length === 0) return Math.max(span / 20, 1e-3);
  // Distance from each outline vertex to the nearest hole vertex is a cheap,
  // decent proxy for wall depth at that corner.
  const thicknesses: number[] = [];
  for (const o of outer) {
    let best = Infinity;
    for (const h of holes) for (const p of h) best = Math.min(best, dist(o, p));
    if (Number.isFinite(best)) thicknesses.push(best);
  }
  if (thicknesses.length === 0) return Math.max(span / 20, 1e-3);
  thicknesses.sort((a, b) => a - b);
  // 20th percentile — biased toward the thinner walls without being the outlier min.
  const t = thicknesses[Math.floor(thicknesses.length * 0.2)];
  return Math.max(t, span / 200, 1e-3);
};

// --- sparse tiled grid ------------------------------------------------------

const TILE_SHIFT = 6;
const TILE_SIZE = 1 << TILE_SHIFT; // 64×64 pixels per tile
const TILE_MASK = TILE_SIZE - 1;
const TILE_AREA = TILE_SIZE * TILE_SIZE;

/**
 * Sparse binary grid: virtual `w×h` pixel space backed by 64×64 tiles that are
 * allocated on first write. Memory scales with WALL pixels, not with the floor
 * bounding box, so big scans keep full resolution.
 */
interface Grid {
  w: number;
  h: number;
  cell: number;
  ox: number; // world x of grid column 0 (with margin)
  oy: number;
  tilesX: number;
  tilesY: number;
  /** Row-major tile index → 64×64 pixel block, or null when never written. */
  tiles: (Uint8Array | null)[];
  allocatedTiles: number;
}

const makeGrid = (w: number, h: number, cell: number, ox: number, oy: number): Grid => {
  const tilesX = Math.ceil(w / TILE_SIZE);
  const tilesY = Math.ceil(h / TILE_SIZE);
  return { w, h, cell, ox, oy, tilesX, tilesY, tiles: new Array<Uint8Array | null>(tilesX * tilesY).fill(null), allocatedTiles: 0 };
};

const get = (g: Grid, x: number, y: number): number => {
  if (x < 0 || y < 0 || x >= g.w || y >= g.h) return 0;
  const t = g.tiles[(y >> TILE_SHIFT) * g.tilesX + (x >> TILE_SHIFT)];
  return t ? t[((y & TILE_MASK) << TILE_SHIFT) | (x & TILE_MASK)] : 0;
};

const tileForWrite = (g: Grid, tx: number, ty: number): Uint8Array => {
  const ti = ty * g.tilesX + tx;
  let t = g.tiles[ti];
  if (!t) {
    t = new Uint8Array(TILE_AREA);
    g.tiles[ti] = t;
    g.allocatedTiles++;
  }
  return t;
};

/**
 * Iterate the filled pixels in GLOBAL row-major order (y ascending, then x
 * ascending) while skipping unallocated tiles — the sparse replacement of the
 * dense `for y { for x }` scan, preserving its visit order.
 */
const forEachFilled = (g: Grid, cb: (x: number, y: number) => void): void => {
  for (let ty = 0; ty < g.tilesY; ty++) {
    const tileRowBase = ty * g.tilesX;
    for (let ly = 0; ly < TILE_SIZE; ly++) {
      const y = (ty << TILE_SHIFT) + ly;
      if (y >= g.h) break;
      const rowBase = ly << TILE_SHIFT;
      for (let tx = 0; tx < g.tilesX; tx++) {
        const tile = g.tiles[tileRowBase + tx];
        if (!tile) continue;
        const baseX = tx << TILE_SHIFT;
        for (let lx = 0; lx < TILE_SIZE; lx++) {
          if (tile[rowBase | lx]) cb(baseX + lx, y);
        }
      }
    }
  }
};

/** Dense w×h snapshot for the debug overlay, or null when the virtual grid is
 *  too large to materialize densely (huge floors — the overlay is skipped). */
const DENSE_DEBUG_MAX_CELLS = 16_000_000;
const denseSnapshot = (g: Grid): Uint8Array | null => {
  if (g.w * g.h > DENSE_DEBUG_MAX_CELLS) return null;
  const out = new Uint8Array(g.w * g.h);
  forEachFilled(g, (x, y) => {
    out[y * g.w + x] = 1;
  });
  return out;
};

// --- rasterization ---------------------------------------------------------

/**
 * Build the binary wall grid: inside outline AND outside every hole = wall.
 *
 * Scanline spans first, pixels second: for each grid row, intersect the row's
 * world-y with the outline to get the covered spans (even-odd rule), then
 * SUBTRACT each hole's spans — all in world coordinates, before any pixel is
 * written. Only the resulting NET wall spans are rasterized, so tiles are
 * allocated exclusively where wall pixels exist (room interiors and voids cost
 * nothing). This is O(rows·verts + wallPixels), and each hole only visits the
 * rows its own bbox covers.
 *
 * Semantics match the previous fill-outline-then-clear-holes implementation:
 * a cell is wall iff its CENTRE is inside the outline (even-odd) and not
 * inside any hole (even-odd per hole, union across holes).
 *
 * Returns null when the wall raster would exceed `maxAllocatedCells` (the
 * caller coarsens and retries — an extreme failsafe, not a normal path).
 */
const rasterize = (outer: Ring, holes: Ring[], cell: number, maxAllocatedCells: number): Grid | null => {
  const b = bounds([outer]);
  const margin = cell * 2; // keep a border so thinning has room
  const ox = b.minx - margin;
  const oy = b.miny - margin;
  const w = Math.max(1, Math.ceil((b.maxx - b.minx + 2 * margin) / cell));
  const h = Math.max(1, Math.ceil((b.maxy - b.miny + 2 * margin) / cell));
  const grid = makeGrid(w, h, cell, ox, oy);

  // Rows whose scanline can cross the ring's y-extent (±1 row of slack).
  const rowRangeOf = (ring: Ring): [number, number] => {
    let miny = Infinity;
    let maxy = -Infinity;
    for (const p of ring) {
      if (p.y < miny) miny = p.y;
      if (p.y > maxy) maxy = p.y;
    }
    const lo = Math.max(0, Math.floor((miny - oy) / cell - 0.5) - 1);
    const hi = Math.min(h - 1, Math.ceil((maxy - oy) / cell - 0.5) + 1);
    return [lo, hi];
  };

  // Sorted x-crossings of `ring` with the scanline at world `wy` (half-open
  // edge rule, matching the classic ray-crossing test). Shared scratch array.
  const xs: number[] = [];
  const crossings = (ring: Ring, wy: number): number[] => {
    xs.length = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const yi = ring[i].y;
      const yj = ring[j].y;
      if (yi > wy !== yj > wy) {
        const xi = ring[i].x;
        const xj = ring[j].x;
        xs.push(xi + ((xj - xi) * (wy - yi)) / (yj - yi));
      }
    }
    xs.sort((p, q) => p - q);
    return xs;
  };

  // Per-row net wall spans in world-x, as flat [s0,e0, s1,e1, ...] arrays.
  const rowSpans: (number[] | null)[] = new Array(h).fill(null);

  // 1. Outline spans (even-odd pairs of sorted crossings).
  const [outerLo, outerHi] = rowRangeOf(outer);
  for (let gy = outerLo; gy <= outerHi; gy++) {
    const wy = oy + (gy + 0.5) * cell;
    const c = crossings(outer, wy);
    if (c.length < 2) continue;
    const spans: number[] = [];
    for (let s = 0; s + 1 < c.length; s += 2) spans.push(c[s], c[s + 1]);
    rowSpans[gy] = spans;
  }

  // Remove [a,b] from a flat span list (interval subtraction).
  const subtractSpan = (row: number[], a: number, bx: number): number[] => {
    const out: number[] = [];
    for (let i = 0; i + 1 < row.length; i += 2) {
      const s = row[i];
      const e = row[i + 1];
      if (e < a || s > bx) {
        out.push(s, e);
        continue;
      }
      if (s < a) out.push(s, a);
      if (e > bx) out.push(bx, e);
    }
    return out;
  };

  // 2. Punch out holes — each hole only touches its own row range, and holes
  //    that don't overlap this component's grid are naturally no-ops.
  for (const hole of holes) {
    const [holeLo, holeHi] = rowRangeOf(hole);
    for (let gy = holeLo; gy <= holeHi; gy++) {
      const row = rowSpans[gy];
      if (!row || row.length === 0) continue;
      const wy = oy + (gy + 0.5) * cell;
      const c = crossings(hole, wy);
      let next = row;
      for (let s = 0; s + 1 < c.length; s += 2) next = subtractSpan(next, c[s], c[s + 1]);
      rowSpans[gy] = next;
    }
  }

  // 3. Rasterize the net spans; cells whose CENTRE lies within a span are wall.
  const maxTiles = Math.max(1, Math.floor(maxAllocatedCells / TILE_AREA));
  for (let gy = 0; gy < h; gy++) {
    const row = rowSpans[gy];
    if (!row) continue;
    const ty = gy >> TILE_SHIFT;
    const rowBase = (gy & TILE_MASK) << TILE_SHIFT;
    for (let i = 0; i + 1 < row.length; i += 2) {
      const gxLo = Math.max(0, Math.ceil((row[i] - ox) / cell - 0.5));
      const gxHi = Math.min(w - 1, Math.floor((row[i + 1] - ox) / cell - 0.5));
      let gx = gxLo;
      while (gx <= gxHi) {
        const tx = gx >> TILE_SHIFT;
        const tile = tileForWrite(grid, tx, ty);
        if (grid.allocatedTiles > maxTiles) return null; // failsafe — caller coarsens
        const segEnd = Math.min(gxHi, ((tx + 1) << TILE_SHIFT) - 1);
        for (; gx <= segEnd; gx++) tile[rowBase | (gx & TILE_MASK)] = 1;
      }
    }
  }
  return grid;
};

// --- Zhang–Suen thinning ---------------------------------------------------
//
// Iteratively peel boundary pixels that aren't needed to preserve connectivity,
// in two sub-passes per iteration, until nothing changes. Classic algorithm;
// the 8-neighbourhood is labelled P2..P9 clockwise from north:
//   P9 P2 P3
//   P8 P1 P4
//   P7 P6 P5
//
// Sparse version: only ACTIVE tiles are scanned each iteration. All allocated
// tiles start active; a tile stays active for the next iteration only if a
// pixel in (or within 1px of) it was cleared — so work shrinks with the
// eroding wall front instead of rescanning the whole plan every pass.

const zhangSuenThin = (g: Grid): void => {
  const { tilesX, tilesY, tiles, w, h } = g;
  const tileCount = tilesX * tilesY;
  let active = new Uint8Array(tileCount);
  let nextActive = new Uint8Array(tileCount);
  for (let i = 0; i < tileCount; i++) if (tiles[i]) active[i] = 1;
  // Reused scratch lists of pixel coordinates to clear this sub-pass.
  const clearX: number[] = [];
  const clearY: number[] = [];
  let changed = true;
  while (changed) {
    changed = false;
    nextActive.fill(0);
    for (let step = 0; step < 2; step++) {
      clearX.length = 0;
      clearY.length = 0;
      for (let ty = 0; ty < tilesY; ty++) {
        for (let tx = 0; tx < tilesX; tx++) {
          const ti = ty * tilesX + tx;
          if (!active[ti]) continue;
          const tile = tiles[ti];
          if (!tile) continue;
          const baseX = tx << TILE_SHIFT;
          const baseY = ty << TILE_SHIFT;
          for (let ly = 0; ly < TILE_SIZE; ly++) {
            const y = baseY + ly;
            if (y < 1 || y > h - 2) continue;
            const rowBase = ly << TILE_SHIFT;
            for (let lx = 0; lx < TILE_SIZE; lx++) {
              if (tile[rowBase | lx] !== 1) continue;
              const x = baseX + lx;
              if (x < 1 || x > w - 2) continue;
              // 8 neighbours P2..P9 (N, NE, E, SE, S, SW, W, NW). Interior
              // pixels read straight from this tile; border pixels go through
              // the cross-tile accessor.
              let p2: number;
              let p3: number;
              let p4: number;
              let p5: number;
              let p6: number;
              let p7: number;
              let p8: number;
              let p9: number;
              if (lx > 0 && lx < TILE_MASK && ly > 0 && ly < TILE_MASK) {
                const up = rowBase - TILE_SIZE;
                const dn = rowBase + TILE_SIZE;
                p2 = tile[up | lx];
                p3 = tile[up | (lx + 1)];
                p4 = tile[rowBase | (lx + 1)];
                p5 = tile[dn | (lx + 1)];
                p6 = tile[dn | lx];
                p7 = tile[dn | (lx - 1)];
                p8 = tile[rowBase | (lx - 1)];
                p9 = tile[up | (lx - 1)];
              } else {
                p2 = get(g, x, y - 1);
                p3 = get(g, x + 1, y - 1);
                p4 = get(g, x + 1, y);
                p5 = get(g, x + 1, y + 1);
                p6 = get(g, x, y + 1);
                p7 = get(g, x - 1, y + 1);
                p8 = get(g, x - 1, y);
                p9 = get(g, x - 1, y - 1);
              }
              const bsum = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9; // # filled neighbours
              if (bsum < 2 || bsum > 6) continue;
              // 0→1 transitions around the ordered ring P2..P9,P2.
              const t = (p2 === 0 && p3 === 1 ? 1 : 0) + (p3 === 0 && p4 === 1 ? 1 : 0) + (p4 === 0 && p5 === 1 ? 1 : 0) + (p5 === 0 && p6 === 1 ? 1 : 0) + (p6 === 0 && p7 === 1 ? 1 : 0) + (p7 === 0 && p8 === 1 ? 1 : 0) + (p8 === 0 && p9 === 1 ? 1 : 0) + (p9 === 0 && p2 === 1 ? 1 : 0);
              if (t !== 1) continue;
              if (step === 0) {
                if (p2 * p4 * p6 !== 0) continue; // P2*P4*P6
                if (p4 * p6 * p8 !== 0) continue; // P4*P6*P8
              } else {
                if (p2 * p4 * p8 !== 0) continue; // P2*P4*P8
                if (p2 * p6 * p8 !== 0) continue; // P2*P6*P8
              }
              clearX.push(x);
              clearY.push(y);
            }
          }
        }
      }
      if (clearX.length > 0) {
        changed = true;
        for (let i = 0; i < clearX.length; i++) {
          const x = clearX[i];
          const y = clearY[i];
          const t = tiles[(y >> TILE_SHIFT) * tilesX + (x >> TILE_SHIFT)];
          if (t) t[((y & TILE_MASK) << TILE_SHIFT) | (x & TILE_MASK)] = 0;
          // Reactivate every tile whose pixels can see this cleared pixel in
          // their 8-neighbourhood (the halo can straddle tile borders).
          const txLo = (x - 1) >> TILE_SHIFT;
          const txHi = Math.min(tilesX - 1, (x + 1) >> TILE_SHIFT);
          const tyLo = (y - 1) >> TILE_SHIFT;
          const tyHi = Math.min(tilesY - 1, (y + 1) >> TILE_SHIFT);
          for (let ny = tyLo; ny <= tyHi; ny++) {
            for (let nx = txLo; nx <= txHi; nx++) nextActive[ny * tilesX + nx] = 1;
          }
        }
      }
    }
    if (changed) {
      const tmp = active;
      active = nextActive;
      nextActive = tmp;
    }
  }
};

// --- trace thinned pixels into polylines ------------------------------------

/** 8-neighbour count of a filled pixel. */
const neighbourCount = (g: Grid, x: number, y: number): number => {
  let c = 0;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (!(dx === 0 && dy === 0) && get(g, x + dx, y + dy)) c++;
  return c;
};

/** List the 8-neighbour filled pixels of (x,y). */
const neighbours = (g: Grid, x: number, y: number): Array<[number, number]> => {
  const out: Array<[number, number]> = [];
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (!(dx === 0 && dy === 0) && get(g, x + dx, y + dy)) out.push([x + dx, y + dy]);
  return out;
};

/**
 * Trace the 1-px skeleton into pixel polylines. We walk each maximal run
 * between "nodes" (endpoints with 1 neighbour, or junctions with ≥3), and also
 * pick up isolated loops with no node. Each edge of the pixel graph is emitted
 * once.
 */
const tracePolylines = (g: Grid): Array<Array<[number, number]>> => {
  const key = (x: number, y: number) => y * g.w + x;
  const visitedEdge = new Set<string>();
  const edgeKey = (a: [number, number], b: [number, number]) => {
    const ka = key(a[0], a[1]);
    const kb = key(b[0], b[1]);
    return ka < kb ? `${ka}-${kb}` : `${kb}-${ka}`;
  };
  const isNode = (x: number, y: number) => {
    const c = neighbourCount(g, x, y);
    return c === 1 || c >= 3;
  };

  const polylines: Array<Array<[number, number]>> = [];

  // Collect all node pixels (sparse row-major scan).
  const nodes: Array<[number, number]> = [];
  forEachFilled(g, (x, y) => {
    if (isNode(x, y)) nodes.push([x, y]);
  });

  // Walk from each node along each unvisited incident edge until the next node.
  const walkFrom = (start: [number, number], first: [number, number]): void => {
    if (visitedEdge.has(edgeKey(start, first))) return;
    const path: Array<[number, number]> = [start, first];
    visitedEdge.add(edgeKey(start, first));
    let prev = start;
    let cur = first;
    // Continue while cur is a simple (degree-2) pixel.
    while (neighbourCount(g, cur[0], cur[1]) === 2 && !isNode(cur[0], cur[1])) {
      const nbrs = neighbours(g, cur[0], cur[1]);
      const prevSnap = prev; // snapshot so the closure doesn't capture a mutated var
      const next = nbrs.find((n) => !(n[0] === prevSnap[0] && n[1] === prevSnap[1]));
      if (!next) break;
      visitedEdge.add(edgeKey(cur, next));
      path.push(next);
      prev = cur;
      cur = next;
    }
    polylines.push(path);
  };

  for (const node of nodes) {
    for (const nb of neighbours(g, node[0], node[1])) {
      if (!visitedEdge.has(edgeKey(node, nb))) walkFrom(node, nb);
    }
  }

  // Pure loops (no node pixel): pick any unvisited filled pixel and walk around.
  const globallyVisited = new Set<number>();
  for (const pl of polylines) for (const p of pl) globallyVisited.add(key(p[0], p[1]));
  forEachFilled(g, (x, y) => {
    if (globallyVisited.has(key(x, y))) return;
    // Walk a closed loop.
    const path: Array<[number, number]> = [[x, y]];
    globallyVisited.add(key(x, y));
    let prev: [number, number] | null = null;
    let cur: [number, number] = [x, y];
    for (let guard = 0; guard < g.w * g.h; guard++) {
      const prevSnap = prev; // snapshot so the closures don't capture a mutated var
      const nbrs = neighbours(g, cur[0], cur[1]).filter((n) => !(prevSnap && n[0] === prevSnap[0] && n[1] === prevSnap[1]));
      const next = nbrs.find((n) => !globallyVisited.has(key(n[0], n[1])));
      if (!next) break;
      globallyVisited.add(key(next[0], next[1]));
      path.push(next);
      prev = cur;
      cur = next;
    }
    if (path.length >= 2) polylines.push(path);
  });

  return polylines;
};

// --- Douglas–Peucker simplification ----------------------------------------

const perpDist = (p: Vec2, a: Vec2, b: Vec2): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 < 1e-18) return dist(p, a);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(p.x - cx, p.y - cy);
};

const douglasPeucker = (pts: Vec2[], tol: number): Vec2[] => {
  if (pts.length < 3) return pts.slice();
  let maxD = 0;
  let idxMax = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = perpDist(pts[i], pts[0], pts[pts.length - 1]);
    if (d > maxD) {
      maxD = d;
      idxMax = i;
    }
  }
  if (maxD <= tol) return [pts[0], pts[pts.length - 1]];
  const left = douglasPeucker(pts.slice(0, idxMax + 1), tol);
  const right = douglasPeucker(pts.slice(idxMax), tol);
  return left.slice(0, -1).concat(right);
};

// --- axis snapping ----------------------------------------------------------

/**
 * Snap a polyline's near-horizontal / near-vertical segments to true axes by
 * collapsing the off-axis coordinate of each segment's endpoints to a shared
 * value. Corners and junctions are reconciled later, globally, by
 * {@link cleanupArcGraph}.
 */
const snapToAxes = (pts: Vec2[], angleDeg: number): Vec2[] => {
  if (pts.length < 2) return pts;
  const cosTol = Math.cos((angleDeg * Math.PI) / 180);
  const out = pts.map((p) => ({ x: p.x, y: p.y }));
  for (let i = 0; i < out.length - 1; i++) {
    const a = out[i];
    const b = out[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    if (Math.abs(dx) / len >= cosTol) {
      const my = (a.y + b.y) / 2;
      a.y = my;
      b.y = my;
    } else if (Math.abs(dy) / len >= cosTol) {
      const mx = (a.x + b.x) / 2;
      a.x = mx;
      b.x = mx;
    }
  }
  return out;
};

/**
 * Global graph cleanup over ALL arcs — this is what removes the "weird
 * structures" (junction triangles, corner chamfer elbows, doubled connectors)
 * that come from each pixel polyline being traced and snapped independently:
 *
 *   1. CLUSTER endpoints that fall within `weld` of each other into one shared
 *      node at the cluster centroid. A junction where several short polylines
 *      meet collapses from a little triangle to a single point.
 *   2. Rewrite every arc to its clustered endpoints; drop arcs that became
 *      degenerate (both ends in the same cluster — the absorbed elbow).
 *   3. Drop exact-duplicate arcs.
 *   4. MERGE collinear runs through degree-2 nodes into one straight arc, so a
 *      straight wall is a single line rather than a chain of short pieces.
 *
 * `weld` defaults to ~1.5 cells: big enough to fuse a junction's near-coincident
 * endpoints, small enough not to merge genuinely separate walls.
 */
const cleanupArcGraph = (arcs: SkeletonArc[], weld: number): SkeletonArc[] => {
  if (arcs.length === 0) return arcs;

  // --- 1. Cluster endpoints within `weld` of each other (union-find). ---
  // Distance-based, not grid-bucketed: a junction's split endpoints fuse no
  // matter how they straddle any grid boundary. A spatial hash over weld-sized
  // cells keeps the neighbour search near-linear.
  const pts: Vec2[] = [];
  for (const a of arcs) {
    pts.push(a.a, a.b);
  }
  const parent = pts.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (i: number, j: number) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[ri] = rj;
  };
  // Spatial hash: bucket key → point indices. Search the 3×3 neighbourhood.
  const hash = new Map<string, number[]>();
  const bkey = (x: number, y: number) => `${Math.floor(x / weld)},${Math.floor(y / weld)}`;
  for (let i = 0; i < pts.length; i++) {
    const k = bkey(pts[i].x, pts[i].y);
    const arr = hash.get(k) ?? [];
    arr.push(i);
    hash.set(k, arr);
  }
  for (let i = 0; i < pts.length; i++) {
    const cx = Math.floor(pts[i].x / weld);
    const cy = Math.floor(pts[i].y / weld);
    for (let gx = cx - 1; gx <= cx + 1; gx++) {
      for (let gy = cy - 1; gy <= cy + 1; gy++) {
        const arr = hash.get(`${gx},${gy}`);
        if (!arr) continue;
        for (const j of arr) {
          if (j <= i) continue;
          if (dist(pts[i], pts[j]) <= weld) union(i, j);
        }
      }
    }
  }
  // Representative position per cluster = centroid of its members.
  const clusterPts = new Map<number, Vec2[]>();
  for (let i = 0; i < pts.length; i++) {
    const r = find(i);
    const arr = clusterPts.get(r) ?? [];
    arr.push(pts[i]);
    clusterPts.set(r, arr);
  }
  const clusterRep = new Map<number, Vec2>();
  for (const [r, arr] of clusterPts) {
    let sx = 0;
    let sy = 0;
    for (const p of arr) {
      sx += p.x;
      sy += p.y;
    }
    clusterRep.set(r, { x: sx / arr.length, y: sy / arr.length });
  }
  // Map an arc endpoint (by its index in `pts`) to its cluster representative.
  // `pts` was filled in arc order, so arc k has a=pts[2k], b=pts[2k+1].
  const repOfIndex = (i: number): Vec2 => clusterRep.get(find(i)) ?? pts[i];

  // --- 2 & 3. Rewrite arcs to clustered nodes; drop degenerate + duplicates. ---
  const seen = new Set<string>();
  const keyOf = (p: Vec2) => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
  let welded: SkeletonArc[] = [];
  for (let k = 0; k < arcs.length; k++) {
    const a = repOfIndex(2 * k);
    const b = repOfIndex(2 * k + 1);
    if (dist(a, b) < weld * 0.5) continue; // collapsed to a point
    const ka = keyOf(a);
    const kb = keyOf(b);
    const ek = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    if (seen.has(ek)) continue;
    seen.add(ek);
    welded.push({ a: { x: a.x, y: a.y }, b: { x: b.x, y: b.y } });
  }

  // --- 4. Merge collinear runs through degree-2 nodes. ---
  welded = mergeCollinearChains(welded);
  return welded;
};

/** Merge arcs meeting at a degree-2 node into a single arc when collinear. */
const mergeCollinearChains = (arcs: SkeletonArc[]): SkeletonArc[] => {
  const keyOf = (p: Vec2) => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
  const adj = new Map<string, SkeletonArc[]>();
  const touch = (k: string, a: SkeletonArc) => {
    const l = adj.get(k) ?? [];
    l.push(a);
    adj.set(k, l);
  };
  for (const a of arcs) {
    touch(keyOf(a.a), a);
    touch(keyOf(a.b), a);
  }
  const removed = new Set<SkeletonArc>();
  const farEnd = (a: SkeletonArc, p: Vec2): Vec2 => (dist(a.a, p) < dist(a.b, p) ? a.b : a.a);
  for (const arc of arcs) {
    if (removed.has(arc)) continue;
    let changed = true;
    while (changed) {
      changed = false;
      const k = keyOf(arc.b);
      const alive = (adj.get(k) ?? []).filter((x) => x !== arc && !removed.has(x));
      if (alive.length !== 1) break; // junction or dead end — stop
      const nxt = alive[0];
      const other = farEnd(nxt, arc.b);
      const d1 = { x: arc.b.x - arc.a.x, y: arc.b.y - arc.a.y };
      const d2 = { x: other.x - arc.b.x, y: other.y - arc.b.y };
      const l1 = Math.hypot(d1.x, d1.y);
      const l2 = Math.hypot(d2.x, d2.y);
      if (l1 < 1e-9 || l2 < 1e-9) break;
      const crossN = (d1.x * d2.y - d1.y * d2.x) / (l1 * l2);
      const dotN = (d1.x * d2.x + d1.y * d2.y) / (l1 * l2);
      if (Math.abs(crossN) >= 0.05 || dotN <= 0) break; // not collinear/forward
      removed.add(nxt);
      arc.b = { x: other.x, y: other.y };
      touch(keyOf(arc.b), arc);
      changed = true;
    }
  }
  return arcs.filter((a) => !removed.has(a));
};

// --- main -------------------------------------------------------------------

/**
 * Compute the raster-thinned skeleton of the wall region and return its arcs in
 * world coordinates.
 */
export function computeRasterThinSkeleton(input: RasterSkeletonInput, options: RasterSkeletonOptions = {}): SkeletonArc[] {
  const outer = input.outer;
  const holes = input.holes ?? [];
  if (outer.length < 3) return [];

  // Cell size: explicit, or derived from the estimated wall thickness. The
  // estimate is O(outerVerts·holeVerts) — only paid when no explicit size is
  // configured.
  let cell = options.cellSize ?? Math.max(estimateWallThickness(outer, holes, bounds([outer])) / 4, 1e-3);

  // The grid is sparse (tiles only where wall pixels exist), so floor size does
  // NOT force a coarser cell — big scans keep full resolution. maxCells is an
  // extreme failsafe on the wall raster itself; realistic plans stay far below.
  const maxCells = options.maxCells ?? 128_000_000;
  let grid = rasterize(outer, holes, cell, maxCells);
  for (let attempt = 0; !grid && attempt < 4; attempt++) {
    cell *= 2;
    console.warn(`rasterThinSkeleton: wall raster exceeds ${maxCells} cells; coarsening cell to ${cell.toFixed(3)}.`);
    grid = rasterize(outer, holes, cell, maxCells);
  }
  if (!grid) return [];

  // Snapshot the filled wall grid before thinning erodes it (for the debug
  // overlay). Dense w×h materialization — skipped (null) on floors whose
  // virtual grid is too large to expand densely.
  const wallSnapshot = options.onDebug ? denseSnapshot(grid) : null;
  if (options.onDebug && !wallSnapshot) {
    console.warn(`rasterThinSkeleton: grid ${grid.w}×${grid.h} too large for the dense debug overlay; skipping snapshot.`);
  }
  zhangSuenThin(grid);
  if (options.onDebug && wallSnapshot) {
    const thinned = denseSnapshot(grid);
    if (thinned) {
      options.onDebug({ w: grid.w, h: grid.h, cell: grid.cell, ox: grid.ox, oy: grid.oy, wall: wallSnapshot, thinned });
    }
  }
  const pixelPolys = tracePolylines(grid);

  const gridRef = grid;
  const toWorld = (px: number, py: number): Vec2 => ({ x: gridRef.ox + (px + 0.5) * cell, y: gridRef.oy + (py + 0.5) * cell });

  const simplifyTol = options.simplifyTolerance ?? cell * 1.5;
  const snapAngle = options.axisSnapAngleDeg ?? 8;

  const arcs: SkeletonArc[] = [];
  for (const poly of pixelPolys) {
    if (poly.length < 2) continue;
    const world = poly.map(([px, py]) => toWorld(px, py));
    const simplified = douglasPeucker(world, simplifyTol);
    const snapped = snapToAxes(simplified, snapAngle);
    for (let i = 0; i < snapped.length - 1; i++) {
      const a = snapped[i];
      const b = snapped[i + 1];
      if (dist(a, b) > 1e-6) arcs.push({ a, b });
    }
  }

  // Global cleanup: weld near-coincident junction/corner endpoints into shared
  // nodes, drop the absorbed elbow stubs, and merge collinear runs. Weld radius
  // ~1.5 cells fuses a junction's split endpoints without merging real walls.
  return cleanupArcGraph(arcs, cell * 1.5);
}
